import { resolveStoredFileUrl } from "utils/storedFileUrl";
// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import { CircularProgress, Icon, Tooltip } from "@mui/material";

// Kaizen Dashboard components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { formatDate } from "utils/formatters";
import dayjs from "dayjs";
import SoftBadge from "components/SoftBadge";
import { MenuContext } from "context/menuContext";
import { useContext, useState } from "react";
import { getUproColor } from "utils/colors";
import { UserContext } from "context/user";

function DocumentacionItem({
  name,
  description,
  value,
  onDelete,
  onEdit,
  readOnly,
  vencimiento,
  file,
  handleAprobar,
  handleRechazar,
  loading,
}) {
  const { menu } = useContext(MenuContext);
  const { verificarAcceso } = useContext(UserContext);
  return (
    <SoftBox key={name} component="li" py={1} mb={1}>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center">
        <SoftBox display="flex" alignItems="center">
          <Tooltip
            title={
              file.estado === "Aprobado"
                ? "El archivo fue aprobado"
                : file.estado === "Rechazado"
                  ? "El archivo fue rechazado"
                  : file.estado === "Pendiente"
                    ? "El archivo está pendiente de revisión"
                    : !file.estado
                      ? "Archivo subido"
                      : "No hay ningún archivo seleccionado"
            }
            placement="top"
          >
            <SoftButton
              iconOnly
              circular
              variant="gradient"
              color={
                file.estado === "Aprobado"
                  ? "success"
                  : file.estado === "Rechazado"
                    ? "error"
                    : file.estado === "Pendiente"
                      ? "warning"
                      : !file.estado
                        ? "info"
                        : "secondary"
              }
            >
              <Icon fontSize="medium">
                {file.estado === "Aprobado"
                  ? "done"
                  : file.estado === "Rechazado"
                    ? "close"
                    : file.estado === "Pendiente"
                      ? "pending_actions"
                      : !file.estado
                        ? "file_upload"
                        : "attach_file"}
              </Icon>
            </SoftButton>
          </Tooltip>
          <Tooltip title={name} placement="top">
            <SoftBox
              display="flex"
              flexDirection="column"
              sx={{
                cursor: "pointer",
              }}
              ml={1}
            >
              <SoftBox display="flex" alignItems="center">
                <SoftTypography variant="button" fontWeight="medium" gutterBottom>
                  {name.length > 40 ? `${name.substring(0, 40)}...` : name}
                </SoftTypography>

                {vencimiento && (
                  <>
                    {dayjs(vencimiento).isSame(dayjs(), "day") ? (
                      <SoftBox display="flex" alignItems="center" mb={1} ml={1}>
                        <SoftBadge badgeContent="Vence hoy" color="error" />
                      </SoftBox>
                    ) : dayjs(vencimiento).isBefore(dayjs().subtract(1, "day")) ? (
                      <SoftBox display="flex" alignItems="center" mb={1} ml={1}>
                        <SoftBadge
                          badgeContent={`Vencido el ${formatDate(vencimiento, "dd MMMM de yyyy")}`}
                          color="secondary"
                        />
                      </SoftBox>
                    ) : dayjs(vencimiento).isBefore(dayjs().add(30, "day")) ? (
                      <SoftBox display="flex" alignItems="center" mb={1} ml={1}>
                        <SoftBadge
                          badgeContent={`Vence en ${dayjs(vencimiento).diff(dayjs(), "day")} días`}
                          color="warning"
                        />
                      </SoftBox>
                    ) : (
                      <SoftBox display="flex" alignItems="center" mb={1} ml={1}>
                        <SoftBadge
                          badgeContent={`Vence el ${formatDate(vencimiento, "dd MMMM de yyyy")}`}
                          color="success"
                        />
                      </SoftBox>
                    )}
                  </>
                )}
              </SoftBox>
              <SoftTypography variant="caption" color="text">
                {description}
              </SoftTypography>
            </SoftBox>
          </Tooltip>

        </SoftBox>
        <SoftBox display="flex" justifyContent="center" alignItems="center">
          {file.estado === "Pendiente" && verificarAcceso(57) && !readOnly && (
            <SoftBox mr={1}>
              <Tooltip title="Rechazar" placement="top">
                <SoftButton
                  color="error"
                  size="small"
                  iconOnly
                  circular
                  onClick={handleRechazar}
                  disabled={loading}
                >
                  <Icon sx={{ fontWeight: "bold" }}>close</Icon>
                </SoftButton>
              </Tooltip>
            </SoftBox>
          )}
          {file.estado === "Pendiente" && verificarAcceso(57) && !readOnly && (
            <SoftBox mr={1}>
              <Tooltip title="Aprobar" placement="top">
                <SoftButton
                  color="success"
                  size="small"
                  iconOnly
                  circular
                  onClick={handleAprobar}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={15} color="white" />
                  ) : (
                    <Icon sx={{ fontWeight: "bold" }}>done</Icon>
                  )}
                </SoftButton>
              </Tooltip>
            </SoftBox>
          )}
          {!readOnly && (
            <SoftBox>
              <Tooltip title="Opciones" placement="top">
                <SoftButton
                  color="uproGreen"
                  size="small"
                  iconOnly
                  circular
                  onClick={(event) => {
                    menu({
                      open: event.currentTarget,
                      align: "right",
                      options: [
                        {
                          name: "Abrir",
                          icon: { icon: "launch", color: getUproColor("uproGreen") },
                          onClick: async () => {
                            window.open(resolveStoredFileUrl(file.file), "_blank");
                          },
                        },
                        {
                          name: "Descargar",
                          icon: { icon: "download", color: getUproColor("uproGreen") },
                          onClick: async () => {
                            const archivo = await fetch(resolveStoredFileUrl(file.file));
                            const blob = await archivo.blob();
                            const url = URL.createObjectURL(blob);

                            const a = document.createElement("a");
                            a.href = url;
                            a.download = name;
                            a.click();
                          },
                        },
                        {
                          name: "Eliminar",
                          icon: { icon: "delete", color: "error" },
                          disabled: !verificarAcceso(57),
                          onClick: onDelete,
                        },
                        {
                          name: "Editar Fecha de vencimiento",
                          icon: { icon: "calendar_today", color: getUproColor("uproGreen") },
                          onClick: onEdit,
                        }
                      ],
                      handleClose: () => { },
                    });
                  }}
                  disabled={loading}
                >
                  <Icon sx={{ fontWeight: "bold" }}>more_vert</Icon>
                </SoftButton>
              </Tooltip>
            </SoftBox>
          )}
          {readOnly && (
            <SoftBox>
              <Tooltip title="Ver archivo" placement="top">
                <SoftButton
                  color="uproGreen"
                  size="small"
                  iconOnly
                  circular
                  onClick={async () => {
                    window.open(resolveStoredFileUrl(file.file), "_blank");
                  }}
                  disabled={loading}
                >
                  <Icon sx={{ fontWeight: "bold" }}>visibility</Icon>
                </SoftButton>
              </Tooltip>
            </SoftBox>
          )}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}
// Typechecking props of the DocumentacionItem
DocumentacionItem.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  readOnly: PropTypes.bool,
  vencimiento: PropTypes.string,
  file: PropTypes.object,
  handleAprobar: PropTypes.func,
  handleRechazar: PropTypes.func,
  loading: PropTypes.bool,
};

export default DocumentacionItem;
